import type {
  OrderProductItem,
  OrderProductInvoiceLine,
} from '@/lib/services/line-items/line-items.types'
import ImageLightboxPreviewTrigger from '@/components/media/ImageLightboxPreviewTrigger'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Typography } from '@/components/ui/typography'
import { usePathHelper } from '@/hooks/usePathHelper'
import { ReturnFulfillmentStatus } from '@/lib/enums/ReturnFulfillmentStatus'
import { formatLineItemPreview, getLineItemDisplay } from '@/lib/line-item-display'
import { cn, formatCurrency, getEnumDisplayName, parseCurrency } from '@/lib/utils'
import { Link } from '@tanstack/react-router'
import { LogsIcon } from 'lucide-react'

type OrderItemProductCardProps = {
  item: OrderProductInvoiceLine
  quantity?: number
  lineQuantity?: number
  lineUnitOffset?: number
  isReturned?: boolean
  handleViewItemDetailsClick: (item: OrderProductItem) => void
  relationship?: 'root' | 'child'
}

function OrderItemProductCard({
  item,
  quantity = 1,
  lineQuantity = quantity,
  lineUnitOffset = 0,
  isReturned = false,
  handleViewItemDetailsClick,
  relationship = 'root',
}: OrderItemProductCardProps) {
  const path = usePathHelper()
  const salesOrderItem = item.sales_order_item ?? null
  const display = getLineItemDisplay(salesOrderItem ?? item)
  const productId = display.productId
  const shouldLinkToProduct = productId && salesOrderItem?.variant_details?.kind === 'catalog'
  const displayUnitPrice = parseCurrency(item.amount)
  const grossTotal = parseCurrency(item.gross_total)
  // Allocate the supplied total across visual rows; this never chooses or reconstructs order pricing.
  const subtotalThrough = (units: number) =>
    Math.round((grossTotal * 100 * units) / lineQuantity) / 100
  const displayLineTotal =
    subtotalThrough(lineUnitOffset + quantity) - subtotalThrough(lineUnitOffset)
  const image = display.image
  const isChild = relationship === 'child'
  const detailPreviewSource = [
    { label: 'Notes', value: display.notes },
    { label: 'Recipe', value: display.recipe },
    { label: 'Description', value: display.description },
  ].find(({ value }) => Boolean(value))
  const detailPreview = detailPreviewSource
    ? {
        ...detailPreviewSource,
        value: formatLineItemPreview(detailPreviewSource.value),
      }
    : null

  return (
    <div
      className={cn('w-full', isChild ? 'py-3 pr-4 sm:pr-5' : 'px-4 py-4 sm:px-5')}
      data-relationship={relationship}
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <ImageLightboxPreviewTrigger
            media={image}
            alt={display.primaryName || 'Product image'}
            caption={display.primaryName || 'Product image'}
            frameClassName={cn(
              'aspect-square shrink-0 rounded-xl border border-border/60 bg-muted/35 shadow-sm',
              isChild ? 'h-16 w-16' : 'h-[80px] w-[80px]'
            )}
            imageClassName="object-cover"
          />

          <div className="min-w-0 flex-1 space-y-1.5">
            <div className="flex flex-wrap items-baseline gap-2">
              {shouldLinkToProduct ? (
                <Link {...path.products.show(productId).link}>
                  <Typography
                    as={isChild ? 'span' : 'h5'}
                    variant={isChild ? 'bodySmall' : 'cardTitle'}
                    decoration={isReturned ? 'lineThrough' : 'none'}
                    tone={isReturned ? 'muted' : 'default'}
                    weight="semibold"
                    interaction="hoverUnderline"
                    className="whitespace-pre-line"
                  >
                    {display.primaryName}
                  </Typography>
                </Link>
              ) : (
                <Typography
                  as={isChild ? 'span' : 'h5'}
                  variant={isChild ? 'labelStrong' : 'cardTitle'}
                  decoration={isReturned ? 'lineThrough' : 'none'}
                  tone={isReturned ? 'muted' : 'default'}
                  className="whitespace-pre-line"
                >
                  {display.primaryName}
                </Typography>
              )}
              {isReturned ? (
                <Badge variant="destructive" size="sm">
                  {item.isRemoved ? 'Removed' : 'Returned'}
                </Badge>
              ) : null}
              {display.badgeLabel ? <Badge variant="outline">{display.badgeLabel}</Badge> : null}
            </div>

            {detailPreview ? (
              <p className="line-clamp-2 text-[0.9375rem]/6 text-muted-foreground">
                <span className="font-medium text-foreground">{detailPreview.label}:</span>{' '}
                {detailPreview.value}
              </p>
            ) : null}
            {quantity > 1 ? (
              <Typography variant="label" tone="muted">
                {`Unit ${formatCurrency(displayUnitPrice)}`}
              </Typography>
            ) : null}

            {/* A removed unit was never sent back: no return to track. */}
            {isReturned && !item.isRemoved ? (
              <Badge variant="secondary" size="sm">
                {getEnumDisplayName(ReturnFulfillmentStatus, item.returnFulfillmentStatus) ??
                  'Return Status Unknown'}
              </Badge>
            ) : null}
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-border/60 pt-3 sm:min-w-[132px] sm:flex-col sm:items-end sm:border-t-0 sm:border-l sm:pl-4 sm:pt-0">
          <div className="text-left sm:text-right">
            <Typography variant="label" tone="muted" className="tabular-nums">
              {`Qty ${quantity}`}
            </Typography>
            <Typography variant="h5" className="mt-1">
              {formatCurrency(displayLineTotal)}
            </Typography>
          </div>

          {!isReturned ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                if (salesOrderItem) {
                  handleViewItemDetailsClick(salesOrderItem)
                }
              }}
              aria-label={`View ${display.primaryName} details`}
            >
              <LogsIcon className="size-4" />
              Details
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  )
}

export default OrderItemProductCard
